import { forwardRef, useMemo, useRef, useState } from "react";

import {
  completarAutorizacion,
  quienEsPatente,
  registrarEntrada,
  sumarAutoPersonal,
} from "../../services/vigilancia";
import {
  adentroDe,
  afueraNoVigente,
  autorizadaExacta,
  avisoDeError,
  faltaCompletar,
  haceCuanto,
  patenteBusqueda,
  sugerenciaDeAutorizado,
  sugerirPorNombre,
  sugerirPorPatente,
} from "../../shared/autosAutorizados";
import { diaAr, horaAr } from "../../shared/fechaAr";
import Patente from "../funcionales/Patente";
import Sugerencias from "./Sugerencias";

/* ── Registrar una entrada ──────────────────────────────────────────────────
 *
 * ── CASI COMO UN AUTOCOMPLETAR (Guillermo, tickets 389 y 394, 13/10) ───────
 *
 * «Que cargando en el campo patente, la misma ya aparezca y también la persona
 * vinculada a esa patente. Y lo mismo en sentido inverso: cargando los datos
 * del personal en el campo nombre, debería aparecer la patente». Mientras se
 * escribe, debajo del campo aparecen los autos que coinciden —chapa, nombre y
 * sector— y con un toque queda elegido: patente y nombre completos, listo para
 * registrar. Si se termina de escribir una patente que está en la lista, se
 * elige sola, sin tocar nada.
 *
 * La lista va DEBAJO del campo y empuja el resto, no flota encima: en el
 * celular, con el teclado abierto, una lista flotante tapa justo lo que se
 * quiere tocar.
 *
 * ── AL AUTO AUTORIZADO NO SE LE PIDE NADA MÁS ──────────────────────────────
 *
 * En la captura de Guillermo, «A quién viene / motivo» está tachado: para el
 * personal no hace falta. Elegido un auto de la lista, ese campo desaparece y
 * la entrada se guarda con el sector (o, si es una autorización de afuera, con
 * el motivo por el que se la dio), que es lo que él escribía a mano. El DNI
 * tampoco se pide al personal: «ese dato figura en otra base». A un
 * desconocido o proveedor el formulario le pide todo, como siempre.
 *
 * ── LO QUE YA ENTRÓ Y NO ESTÁ EN LA LISTA ──────────────────────────────────
 *
 * «Esos 17 no son todos: faltan algunos». Un auto que entró en los últimos
 * días aparece igual al escribir, marcado «no está en autorizados», y completa
 * lo que se anotó aquella vez. Registrada la entrada de un auto que no está en
 * la lista, se ofrece sumarlo a la lista como fijo con un toque: la próxima
 * vez se completa solo.
 *
 * Pero NO a cualquiera: no al que se le tomó el documento (es un desconocido o
 * un proveedor, no alguien de la casa), ni a una patente que tiene una
 * autorización de afuera que venció o se dio de baja (eso la volvería una
 * autorización sin vencimiento: la renueva quien autoriza). El botón dice
 * «Sumar a autorizados como fijo»: que es de la casa y vale sin vencimiento,
 * que es lo que se está afirmando. (Hasta el ticket 441 decía «Sumar a autos
 * del personal», el nombre de una lista que ya no existe.)
 *
 * ── SI YA FIGURA ADENTRO ───────────────────────────────────────────────────
 *
 * Si la patente elegida ya está en «Adentro ahora», se avisa y se ofrece
 * registrarle la salida ahí mismo: dos entradas abiertas del mismo auto dejan
 * la lista de adentro mintiendo.
 *
 * El nombre se puede pisar a mano —a veces maneja otro— y la entrada sigue
 * ligada a la autorización de esa patente.
 *
 * ── LO QUE NICO NO SABÍA, LO COMPLETA LA BARRERA (ticket 521, 26/10) ───────
 *
 * Nico autoriza a veces sólo por nombre («Carlos Novelli, pedido de
 * Tráfico»): no sabe con qué auto viene. Elegida esa autorización por el
 * nombre, la patente que se escribe NO la suelta —antes sí: la entrada de
 * «NOVELLY» con IHW027 quedó suelta el 26/10— y, registrada la entrada, la
 * patente y el documento que faltaban quedan guardados en la autorización
 * (`completarAutorizacion`). Lo que ya estaba cargado no se pisa. Si eso
 * falla, la entrada ya está anotada y no se pierde: sólo no se completó. */

const quienCorto = (v) => String(v ?? "").split("@")[0].trim();

const vacio = { patente: "", nombre: "", dni: "", aQuien: "", como: "auto" };

const FormEntrada = forwardRef(function FormEntrada(
  { autorizados = [], recientes = [], adentro = [], noVigentes = [], puedeSumar = false,
    onRegistrado, onSalida, onSumado },
  refPatente,
) {
  const [f, setF] = useState(vacio);
  const [elegido, setElegido] = useState(null); // la sugerencia tocada
  const [activo, setActivo] = useState(null); // "patente" | "nombre"
  const [resuelto, setResuelto] = useState(null); // lo que dijo la base (flota)
  const [nombreManual, setNombreManual] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [hecho, setHecho] = useState(null); // la última entrada registrada
  const [sumando, setSumando] = useState(false);
  const [sumado, setSumado] = useState("");
  const [saliendo, setSaliendo] = useState(false);
  // Para que una respuesta de la base que llega tarde no pise lo elegido.
  const elegidoRef = useRef(null);

  const fuentes = useMemo(
    () => ({ autorizados, recientes, adentro }),
    [autorizados, recientes, adentro],
  );

  const sugerencias = useMemo(() => {
    if (elegido) return [];
    if (activo === "patente") return sugerirPorPatente(f.patente, fuentes);
    if (activo === "nombre") return sugerirPorNombre(f.nombre, fuentes);
    return [];
  }, [elegido, activo, f.patente, f.nombre, fuentes]);

  const poner = (cambios) => setF((v) => ({ ...v, ...cambios }));

  const elegir = (s, { pisarNombre = true } = {}) => {
    elegidoRef.current = s;
    setElegido(s);
    setActivo(null);
    setResuelto(null);
    setHecho(null);
    setSumado("");
    setError(null);
    setF((v) => {
      const deja = !pisarNombre && nombreManual && v.nombre.trim();
      return {
        ...v,
        patente: s.patente || v.patente,
        nombre: deja ? v.nombre : s.nombre,
        aQuien: s.detalle,
        como: s.patente ? "auto" : v.como,
        // Al personal no se le pide el DNI: si había algo escrito, no viaja.
        dni: s.tipo === "autorizado" && s.personal ? "" : v.dni,
      };
    });
    if (pisarNombre || !nombreManual) setNombreManual(false);
  };

  const soltar = () => {
    elegidoRef.current = null;
    setElegido(null);
    setResuelto(null);
    setNombreManual(false);
    setF(vacio);
    setActivo("patente");
    refPatente?.current?.focus?.();
  };

  const cambiarPatente = (valor) => {
    const v = valor.toUpperCase();
    setActivo("patente");
    setResuelto(null);
    setHecho(null);
    setSumado("");
    let suelto = false;
    // Una autorización SIN patente no se suelta al escribirla: la barrera la
    // está completando (ticket 521).
    const completando = elegido?.tipo === "autorizado" && !elegido.patente;
    if (elegido && !completando && patenteBusqueda(v) !== elegido.patente) {
      // Lo elegido ya no es esta patente: se suelta, y lo que había puesto él
      // se va con él (lo escrito a mano se queda).
      const antes = elegido;
      elegidoRef.current = null;
      setElegido(null);
      suelto = true;
      setF((x) => ({
        ...x,
        patente: v,
        nombre: nombreManual ? x.nombre : "",
        aQuien: x.aQuien === antes.detalle ? "" : x.aQuien,
      }));
    } else {
      poner({ patente: v });
    }
    // Terminó de escribir una patente que está en la lista: se elige sola.
    const exacta = autorizadaExacta(v, autorizados);
    if (exacta && (suelto || !elegido || elegido.autorizacion?.id !== exacta.id)) {
      elegir(sugerenciaDeAutorizado(exacta, adentro), { pisarNombre: false });
    }
  };

  const cambiarNombre = (valor) => {
    poner({ nombre: valor });
    setNombreManual(true);
    setActivo("nombre");
    setHecho(null);
    setSumado("");
  };

  /* Al salir de la patente sin haber elegido nada: se le pregunta a la base,
   * que además conoce los camiones de la flota. */
  const alSalirDePatente = async (valor) => {
    if (elegidoRef.current) return;
    if (patenteBusqueda(valor).length < 4) return;
    try {
      const r = await quienEsPatente(valor);
      if (elegidoRef.current) return;
      const deLaLista =
        r.autorizado && r.autorizacionId
          ? autorizados.find((a) => String(a.id) === String(r.autorizacionId))
          : null;
      if (deLaLista) {
        elegir(sugerenciaDeAutorizado(deLaLista, adentro), { pisarNombre: false });
        return;
      }
      setResuelto(r);
      if (r.nombre && !nombreManual) poner({ nombre: r.nombre });
    } catch {
      /* La resolución es una ayuda; si falla, se sigue cargando a mano. */
    }
  };

  const limpiar = () => {
    elegidoRef.current = null;
    setF(vacio);
    setElegido(null);
    setActivo(null);
    setResuelto(null);
    setNombreManual(false);
  };

  const autorizada = elegido?.tipo === "autorizado" ? elegido.autorizacion : null;
  const autorizacionId = autorizada?.id || resuelto?.autorizacionId || null;
  const delPersonal = !!(elegido?.tipo === "autorizado" && elegido.personal);
  const sinPatente = !!(autorizada && !String(autorizada.patente ?? "").trim());
  const sinDni = !!(autorizada && !delPersonal && !String(autorizada.dni ?? "").trim());

  const enviar = async () => {
    setError(null);
    setSumado("");
    if (!f.nombre.trim() && !f.patente.trim()) {
      setError("Poné al menos el nombre o la patente.");
      return;
    }
    setGuardando(true);
    try {
      await registrarEntrada({
        nombre: f.nombre,
        patente: f.patente,
        dni: f.dni,
        aQuien: f.aQuien,
        como: f.como,
        // Si la patente estaba en la lista, el ingreso queda LIGADO a esa
        // autorización: así el registro dice «venía autorizado».
        autorizacionId,
      });
      // Lo que le faltaba a la autorización y se anotó ahora, queda guardado.
      let completado = [];
      let noCompleto = false;
      const faltan = faltaCompletar(autorizada, { patente: f.patente, dni: f.dni });
      if (autorizada && Object.keys(faltan).length > 0) {
        try {
          completado = await completarAutorizacion(autorizada.id, faltan);
        } catch {
          noCompleto = true;
        }
      }
      setHecho({
        completado,
        noCompleto,
        nombre: f.nombre.trim(),
        patente: patenteBusqueda(f.patente),
        sector: f.aQuien.trim(),
        dni: f.dni.trim(),
        autorizado: !!autorizacionId,
        flota: resuelto?.fuente === "flota",
        como: f.como,
        // Tenía una autorización de afuera que ya no vale: no se la ofrece
        // como del personal.
        afueraVencida: !autorizacionId && !!afueraNoVigente(f.patente, noVigentes),
      });
      limpiar();
      onRegistrado?.();
    } catch (e) {
      // Sin señal llega «Failed to fetch»: lo que importa es que NO quedó
      // anotada, y lo escrito sigue en el formulario para volver a tocar.
      setError(
        avisoDeError(e, "No se pudo registrar la entrada", "No quedó anotada: probá de nuevo."),
      );
    } finally {
      setGuardando(false);
    }
  };

  /* «Sumar a autorizados»: con lo que se acaba de registrar, un toque. */
  const sumar = async () => {
    if (!hecho) return;
    setSumando(true);
    setError(null);
    try {
      const r = await sumarAutoPersonal({
        patente: hecho.patente,
        nombre: hecho.nombre,
        sector: hecho.sector,
        dni: hecho.dni,
      });
      setSumado(
        r.yaEstaba
          ? `Esa patente ya estaba en la lista${r.nombre ? ` (${r.nombre})` : ""}.`
          : `Listo: la próxima vez ${hecho.patente} se completa sola.`,
      );
      setHecho(null);
      onSumado?.();
    } catch (e) {
      setError(avisoDeError(e, "No se pudo sumar a autorizados"));
    } finally {
      setSumando(false);
    }
  };

  const yaAdentro =
    elegido?.adentro ||
    (patenteBusqueda(f.patente).length >= 4 ? adentroDe(f.patente, adentro) : null);

  /* La salida desde el aviso: el botón dice «Registrando…» mientras tanto, y
   * el formulario se vacía SÓLO si quedó anotada (si no, se ve el aviso de la
   * salida que no se pudo y lo elegido sigue ahí). */
  const salirDesdeAca = async (id) => {
    if (saliendo || !onSalida) return;
    setSaliendo(true);
    try {
      const ok = await onSalida(id);
      if (ok) limpiar();
    } finally {
      setSaliendo(false);
    }
  };

  // Se ofrece sumar como fijo a la lista lo que entró en auto, con patente
  // y nombre, sin estar en la lista y sin ser un camión de la flota. No al que
  // se le tomó el documento (desconocido o proveedor) ni a una patente con una
  // autorización de afuera vencida o dada de baja.
  const ofrecerSumar =
    puedeSumar && hecho && !hecho.autorizado && !hecho.flota &&
    hecho.patente && hecho.nombre && hecho.como === "auto" &&
    !hecho.dni && !hecho.afueraVencida;

  const listaPatente = activo === "patente" && sugerencias.length > 0;
  const listaNombre = activo === "nombre" && sugerencias.length > 0;

  return (
    <div className="vig-panel" id="vig-registrar">
      <h3 className="docs-sub">Registrar entrada</h3>
      <div className="vig-form">
        <div className="vig-campo">
          <label htmlFor="vig-patente">Patente</label>
          <input
            id="vig-patente"
            ref={refPatente}
            value={f.patente}
            onChange={(e) => cambiarPatente(e.target.value)}
            onBlur={(e) => alSalirDePatente(e.target.value)}
            placeholder="AB123CD"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            enterKeyHint="next"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={listaPatente}
            aria-controls="vig-sug-patente"
          />
          {listaPatente && (
            <Sugerencias id="vig-sug-patente" items={sugerencias} onElegir={elegir} />
          )}
        </div>
        <div className="vig-campo">
          <label htmlFor="vig-nombre">Nombre</label>
          <input
            id="vig-nombre"
            value={f.nombre}
            onChange={(e) => cambiarNombre(e.target.value)}
            placeholder="Quién entra"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={listaNombre}
            aria-controls="vig-sug-nombre"
          />
          {listaNombre && (
            <Sugerencias id="vig-sug-nombre" items={sugerencias} onElegir={elegir} />
          )}
        </div>

        {/* Lo elegido de la lista: quién es y por qué entra. */}
        {elegido && (
          <div className="vig-elegido ancho" role="status">
            {elegido.patente && <Patente dominio={elegido.patente} />}
            <p className="vig-elegido-txt">
              {elegido.tipo === "autorizado" ? (
                elegido.personal ? (
                  <>
                    <b>Auto del personal</b>
                    {elegido.nombre ? ` — ${elegido.nombre}` : ""}
                    {elegido.detalle ? `. ${elegido.detalle}` : ""}
                  </>
                ) : (
                  <>
                    <b>
                      Autorizado
                      {quienCorto(autorizada?.autorizado_por)
                        ? ` por ${quienCorto(autorizada?.autorizado_por)}`
                        : ""}
                    </b>
                    {elegido.nombre ? ` — ${elegido.nombre}` : ""}
                    {elegido.detalle ? `. ${elegido.detalle}` : ""}
                    {sinPatente && f.como === "auto"
                      ? ". Sin patente cargada: anotala y queda guardada."
                      : ""}
                  </>
                )
              ) : (
                <>
                  <b>No está en autorizados</b>
                  {elegido.nombre ? ` — ${elegido.nombre}` : ""}
                  {elegido.registro?.entrada
                    ? `. Entró el ${diaAr(elegido.registro.entrada)}`
                    : ""}
                </>
              )}
            </p>
            <button type="button" className="btn btn--ghost vig-sm" onClick={soltar}>
              Cambiar
            </button>
          </div>
        )}

        {/* El DNI: a los desconocidos y proveedores. Al personal, no. */}
        {!delPersonal && (
          <div className="vig-campo">
            <label htmlFor="vig-dni">Documento</label>
            <input
              id="vig-dni"
              value={f.dni}
              onChange={(e) => poner({ dni: e.target.value })}
              placeholder={
                sinDni
                  ? "DNI (no está cargado: queda guardado)"
                  : autorizada
                    ? "DNI (si hace falta)"
                    : "DNI (desconocidos / proveedores)"
              }
              autoComplete="off"
              inputMode="numeric"
            />
          </div>
        )}
        {/* A quién viene: no se le pide al que ya está autorizado (se guarda
            con su sector o su motivo). */}
        {!autorizada && (
          <div className="vig-campo ancho">
            <label htmlFor="vig-aquien">A quién viene / motivo</label>
            <input
              id="vig-aquien"
              value={f.aQuien}
              onChange={(e) => poner({ aQuien: e.target.value })}
              placeholder="A ver a… / a qué viene"
              autoComplete="off"
            />
          </div>
        )}
        <div className="vig-campo">
          <span>Cómo entra</span>
          <div className="tk-filtros">
            {["auto", "caminando"].map((c) => (
              <button
                key={c}
                type="button"
                className={`tk-chip${f.como === c ? " activo" : ""}`}
                onClick={() => poner({ como: c })}
              >
                {c === "auto" ? "En auto" : "Caminando"}
              </button>
            ))}
          </div>
        </div>
      </div>

      {yaAdentro && (
        <div className="panel-aviso suave vig-ya-adentro" role="status">
          <span>
            Ya figura adentro desde las <b>{horaAr(yaAdentro.entrada)}</b> (
            {haceCuanto(yaAdentro.entrada)}).
          </span>
          {onSalida && (
            <button
              type="button"
              className="btn btn--secondary vig-sm"
              disabled={saliendo}
              onClick={() => salirDesdeAca(yaAdentro.id)}
            >
              {saliendo ? "Registrando…" : "Registrar salida"}
            </button>
          )}
        </div>
      )}

      {/* Lo que resolvió la base sin la lista: un camión de la flota, o nada. */}
      {!elegido && resuelto && resuelto.nombre && (
        <p className="panel-aviso suave">
          {resuelto.autorizado ? (
            <>
              <b>
                Autorizado
                {quienCorto(resuelto.autorizadoPor) ? ` por ${quienCorto(resuelto.autorizadoPor)}` : ""}
              </b>
              {` — ${resuelto.nombre}`}
              {resuelto.motivo ? `. ${resuelto.motivo}` : ""}
            </>
          ) : (
            <>
              Esa patente figura como <b>{resuelto.nombre}</b>
              {resuelto.fuente === "flota" ? " (camión de la flota)" : ""}.
            </>
          )}
        </p>
      )}
      {!elegido && resuelto && !resuelto.nombre && !resuelto.autorizado && f.patente.trim() && (
        <p className="panel-aviso suave">
          Esa patente no figura autorizada ni en la flota. Tomá el documento y
          anotá a qué viene.
        </p>
      )}

      {error && <p className="panel-aviso">{error}</p>}

      <div className="vig-acciones">
        <button
          type="button"
          className="btn btn--primary"
          disabled={guardando}
          onClick={enviar}
        >
          {guardando ? "Registrando…" : "Registrar entrada"}
        </button>
      </div>

      {hecho && (
        <div className="panel-aviso suave vig-hecho" role="status">
          <span>
            Entrada registrada: <b>{hecho.nombre || hecho.patente}</b>
            {hecho.nombre && hecho.patente ? ` (${hecho.patente})` : ""}.
            {ofrecerSumar ? " No está en autorizados." : ""}
            {hecho.afueraVencida
              ? " Tenía una autorización de afuera que ya no vale: la renueva quien autoriza."
              : ""}
            {hecho.completado?.length
              ? ` Quedó guardado en la autorización: ${hecho.completado
                  .map((c) => (c === "dni" ? "el documento" : "la patente"))
                  .join(" y ")}.`
              : ""}
            {hecho.noCompleto
              ? " No se pudo guardar en la autorización lo que faltaba: avisale a quien autorizó."
              : ""}
          </span>
          {ofrecerSumar && (
            <button
              type="button"
              className="btn btn--secondary vig-sm"
              disabled={sumando}
              onClick={sumar}
            >
              {sumando ? "Sumando…" : "Sumar a autorizados como fijo"}
            </button>
          )}
        </div>
      )}
      {sumado && (
        <p className="panel-aviso suave" role="status">
          {sumado}
        </p>
      )}
    </div>
  );
});

export default FormEntrada;
